import type { ReactNode } from 'react';
import type { NavView } from '../lib/destinations.js';

/**
 * B2 — the "Today" home: the default landing that answers "where does my attention
 * go right now," ordered by the operator's 4 goals (zero-inbox · check-ins ·
 * LinkedIn engagement · requests). Pure presentation — UnifiedDashboard supplies the
 * goal cards from the SAME count-truth sources the rail badges use (no new fetch,
 * no new API), and only includes outreach goals whose hub is armed.
 */
export interface TodayGoal {
  key: string;
  label: string;
  icon: ReactNode;
  /** Open/pending count (undefined while loading → shows "—"). */
  count: number | undefined;
  /** Items the automation/operator cleared today (omitted where not tracked). */
  doneToday?: number;
  /** The row action verb ("Triage" / "Review" / "Engage"). */
  cta: string;
  view: NavView;
}

export function TodayView({
  goals,
  onChangeView,
  beforeGrid,
}: {
  goals: TodayGoal[];
  onChangeView: (next: NavView) => void;
  /** batch-290 — optional slot between the header and the goal grid (Today's meetings). */
  beforeGrid?: ReactNode;
}) {
  const totalOpen = goals.reduce((n, g) => n + (g.count ?? 0), 0);
  const totalDone = goals.reduce((n, g) => n + (g.doneToday ?? 0), 0);
  return (
    <div className="today-view" data-testid="today-view">
      <header className="today-head">
        <h1 className="today-title">Today</h1>
        <p className="today-sub">
          {totalOpen === 0 ? "You're all caught up." : `${totalOpen} items need you`}
          {totalDone > 0 ? ` · ${totalDone} cleared today` : ''}
        </p>
      </header>
      {beforeGrid}
      <div className="today-grid">
        {goals.map((g) => (
          <button
            key={g.key}
            type="button"
            className="today-card"
            data-testid={`today-card-${g.key}`}
            onClick={() => onChangeView(g.view)}
            aria-label={`${g.label}${g.count === undefined ? '' : `: ${g.count} open`}. ${g.cta}.`}
          >
            <span className="today-card-head">
              <span className="today-card-icon" aria-hidden="true">
                {g.icon}
              </span>
              <span className="today-card-label">{g.label}</span>
            </span>
            <span className="today-card-count">{g.count ?? '—'}</span>
            <span className="today-card-foot">
              <span className="today-card-done">
                {g.doneToday ? `${g.doneToday} done today` : ''}
              </span>
              <span className="today-card-cta">{g.cta} →</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
